/** Plant Register status labels plus the header-bridge event names for the
 *  /plant screen. Co-located for the same reason employers/constants.ts gives
 *  for its own bridge: both sides (UnifiedAppHeader / AuthenticatedController
 *  and PlantRegisterView) already need the status labels, so one import
 *  covers the option lists and removes untyped string-literal event names. */

import type { PlantAsset, PlantAssetStatus } from '../../types/plant';
import { lolerDateMissing, lolerWarning } from './plantAssetUtils';

export const PLANT_STATUS_FILTER_ALL = 'all' as const;
/** Not a status: a machine in use that needs its LOLER exam booked, has let it lapse, or has no exam date recorded. */
export const PLANT_STATUS_FILTER_EXAM_DUE = 'exam_due' as const;
export type PlantStatusFilter =
  | PlantAssetStatus
  | typeof PLANT_STATUS_FILTER_ALL
  | typeof PLANT_STATUS_FILTER_EXAM_DUE;

export const PLANT_STATUS_FILTER_OPTIONS: Array<{ value: PlantStatusFilter; label: string }> = [
  { value: PLANT_STATUS_FILTER_ALL, label: 'All Plant' },
  { value: 'active',      label: 'Active' },
  { value: 'off_hire',    label: 'Off Hire' },
  { value: 'quarantined', label: 'Quarantined' },
  { value: PLANT_STATUS_FILTER_EXAM_DUE, label: 'Exam Due' },
];

export const PLANT_STATUS_LABEL: Record<PlantAssetStatus, string> = {
  active:      'Active',
  off_hire:    'Off Hire',
  quarantined: 'Quarantined',
};

/** Mirrors PLANT_CHECK_STATUS_TOKEN_CLASS in plantCheckUtils.ts so the register
 *  badge and the check-sheet badge read as one system. Quarantined reuses the
 *  destructive token, same call as a rating-3 check: a quarantined machine
 *  does not go out. */
export const PLANT_STATUS_TOKEN_CLASS: Record<PlantAssetStatus, string> = {
  active:      'border-status-success-border bg-status-success-bg text-status-success-foreground hover:bg-status-success-bg',
  off_hire:    'border-status-neutral-border bg-status-neutral-bg text-status-neutral-foreground hover:bg-status-neutral-bg',
  quarantined: 'border-status-danger-border bg-status-danger-bg text-status-danger-foreground hover:bg-status-danger-bg',
};

/** Off-hire and quarantined machines are not lifting, so an exam on them is not a to-do. */
export function matchesPlantStatusFilter(
  asset: Pick<PlantAsset, 'status' | 'lolerRequired' | 'lolerNextExam'>,
  filter: PlantStatusFilter,
): boolean {
  if (filter === PLANT_STATUS_FILTER_ALL) return true;
  if (filter === PLANT_STATUS_FILTER_EXAM_DUE) {
    return asset.status === 'active' && asset.lolerRequired
      && (lolerWarning(asset.lolerNextExam) !== null || lolerDateMissing(asset));
  }
  return asset.status === filter;
}

/** Narrows a stored string back to a filter, so a value written by an older or
 *  newer build can never leave the register on a filter it cannot render. */
export function parsePlantStatusFilter(raw: unknown): PlantStatusFilter {
  const match = PLANT_STATUS_FILTER_OPTIONS.find(o => o.value === raw);
  return match ? match.value : PLANT_STATUS_FILTER_ALL;
}

/** Machines the Exam Due view would list. */
export function countExamDue(assets: Array<Pick<PlantAsset, 'status' | 'lolerRequired' | 'lolerNextExam'>>): number {
  return assets.filter(a => matchesPlantStatusFilter(a, PLANT_STATUS_FILTER_EXAM_DUE)).length;
}

/** Filter options with the Exam Due count appended only when something is due, so an empty view is not announced as "(0)". */
export function plantFilterOptions(examDueCount: number): typeof PLANT_STATUS_FILTER_OPTIONS {
  if (examDueCount <= 0) return PLANT_STATUS_FILTER_OPTIONS;
  return PLANT_STATUS_FILTER_OPTIONS.map(o =>
    o.value === PLANT_STATUS_FILTER_EXAM_DUE ? { ...o, label: `${o.label} (${examDueCount})` } : o);
}

// ─── Header bridge events ───────────────────────────────────────────────────

/** Native + / header + fires this; PlantRegisterView opens a blank record. */
export const PLANT_CREATE_EVENT = 'plant-register:create';
/** Back chevron / Escape-from-drill-in; clears the selection back to the table. */
export const PLANT_CLEAR_SELECTION_EVENT = 'plant-register:clear-selection';
/** AuthenticatedController fires this once a /plant/[id] deep link resolves. */
export const PLANT_SELECT_EVENT = 'plant-register:select';
export const PLANT_SEARCH_EVENT = 'plant-register:search';
export const PLANT_FILTER_EVENT = 'plant-register:filter';
export const PLANT_DETAIL_SAVE_EVENT = 'plant-register:detail-save';
export const PLANT_DETAIL_CANCEL_EVENT = 'plant-register:detail-cancel';
/** PlantListState broadcast — see below. */
export const PLANT_STATE_EVENT = 'plant-register:state-change';
/** PlantDetailState broadcast — see below. */
export const PLANT_DETAIL_STATE_EVENT = 'plant-register:detail-state-change';

/** What PlantRegisterView broadcasts on every list/selection change. id and
 *  label are what the URL and header title need to name the open record; the
 *  header only reads listVisible/statusFilter/selectedLabel. */
export interface PlantListState {
  listVisible: boolean;
  statusFilter: PlantStatusFilter;
  /** Machines in the Exam Due view, over the whole register rather than the current filter. */
  examDueCount: number;
  selectedId: string | null;
  selectedLabel: string | null;
}

/** A drilled-in machine's unsaved-changes state, same shape as the employer
 *  profile bridge. isCreating distinguishes a still-unsaved new machine, whose
 *  Save reads "Create" rather than "Save". */
export interface PlantDetailState {
  hasChanges: boolean;
  isSubmitting: boolean;
  isCreating: boolean;
}
